import { computed, readonly, shallowRef } from 'vue'
import type { AuthSessionPayload } from './session'

export type AuthRecoveryReason = 'auth_required' | 'expired' | 'sign_in'
type RecoveryState =
  | { phase: 'idle'; notice?: string }
  | { phase: 'required' | 'dismissed' | 'authenticating' | 'reconciling' | 'failed'; reason: AuthRecoveryReason; error?: string }

export interface AuthenticationDependencies {
  getSession(): AuthSessionPayload | null
  installSession(payload: AuthSessionPayload): boolean
  suspend(previousSession: AuthSessionPayload | null): void
  clearUserData(): void
  prepareRedirect(previousSession: AuthSessionPayload | null): void
  reconcile(payload: AuthSessionPayload, signal: AbortSignal): Promise<{ notice: string } | void>
}

/** One owner for completion; browser transports and views never declare login ready. */
export function createAuthenticationRecovery(dependencies: AuthenticationDependencies) {
  const state = shallowRef<RecoveryState>({ phase: 'idle' })
  let previousSession: AuthSessionPayload | null = null
  let attempt: AbortController | null = null

  function requireAuthentication(reason: AuthRecoveryReason, session = dependencies.getSession()): boolean {
    if (state.value.phase === 'required') return false
    previousSession ??= session
    attempt?.abort()
    state.value = { phase: 'required', reason }
    dependencies.suspend(previousSession)
    return true
  }

  function beginAuthentication(): AbortSignal {
    previousSession ??= dependencies.getSession()
    const reason = state.value.phase === 'idle' ? 'sign_in' : state.value.reason
    attempt?.abort()
    attempt = new AbortController()
    state.value = { phase: 'authenticating', reason }
    dependencies.suspend(previousSession)
    return attempt.signal
  }

  function assertCurrent(signal: AbortSignal) {
    if (signal.aborted || signal !== attempt?.signal) {
      throw new DOMException('Authentication was superseded.', 'AbortError')
    }
  }

  function failAuthentication(signal: AbortSignal, error: unknown) {
    if (signal.aborted || signal !== attempt?.signal || state.value.phase === 'idle') return
    state.value = { phase: 'failed', reason: state.value.reason, error: error instanceof Error ? error.message : String(error) }
  }

  async function completeAuthentication(signal: AbortSignal, payload: AuthSessionPayload) {
    assertCurrent(signal)
    const reason = state.value.phase === 'idle' ? 'sign_in' : state.value.reason
    try {
      if (previousSession && (previousSession.user.sub !== payload.user.sub || previousSession.user.org_id !== payload.user.org_id)) {
        dependencies.clearUserData()
      }
      state.value = { phase: 'reconciling', reason }
      if (!dependencies.installSession(payload)) throw new Error('Received an invalid login response. Try again.')
      // Reconciliation may partially load data before failing. Future retries
      // must compare against the identity that now owns that data.
      previousSession = payload
      const result = await dependencies.reconcile(payload, signal)
      assertCurrent(signal)
      previousSession = null
      state.value = { phase: 'idle', notice: result?.notice }
    } catch (error) {
      failAuthentication(signal, error)
      throw error
    }
  }

  function dismissAuthentication() {
    attempt?.abort()
    if (state.value.phase !== 'idle') {
      dependencies.suspend(previousSession)
      state.value = { phase: 'dismissed', reason: state.value.reason }
    }
  }

  function resetAuthentication() {
    attempt?.abort()
    previousSession = null
    state.value = { phase: 'idle' }
  }

  return {
    state: readonly(state),
    busy: computed(() => state.value.phase === 'authenticating' || state.value.phase === 'reconciling'),
    blocked: computed(() => state.value.phase !== 'idle' && state.value.phase !== 'dismissed'),
    requireAuthentication, beginAuthentication, completeAuthentication, failAuthentication, dismissAuthentication, resetAuthentication,
    cancelAuthentication(signal: AbortSignal) {
      if (signal === attempt?.signal) dismissAuthentication()
    },
    prepareRedirect: () => dependencies.prepareRedirect(previousSession ?? dependencies.getSession()),
    clearUserData: () => dependencies.clearUserData(),
  }
}

let dependencies: AuthenticationDependencies
export function configureAuthentication(dependenciesToUse: AuthenticationDependencies) {
  dependencies = dependenciesToUse
}

export const authenticationRecovery = createAuthenticationRecovery({
  getSession: () => dependencies.getSession(),
  installSession: payload => dependencies.installSession(payload),
  suspend: previousSession => dependencies.suspend(previousSession),
  clearUserData: () => dependencies.clearUserData(),
  prepareRedirect: previousSession => dependencies.prepareRedirect(previousSession),
  reconcile: (payload, signal) => dependencies.reconcile(payload, signal),
})
